import React, { JSX, useState } from 'react';
import {
  IconInfoCircle,
  IconKeyframes,
  IconMap,
  IconMinus,
  IconPlus,
  IconRoute,
  IconX,
} from '@tabler/icons-react';
import LayersAndFilters from './panelcontent/layersandfilters';
import MapKey from './panelcontent/mapkey';
import styles from './actiondrawer.module.css';

import 'maplibre-gl/dist/maplibre-gl.css';

import { Text } from '@mantine/core';
import { Tooltip } from '@/components/tooltip/tooltip';
import { useMapContext } from '@/contexts/MapContext';
import { NetworkSimulation } from './panelcontent/NetworkSimulation';

const PanelTitle = ({
  title,
  handleClosePanel,
}: {
  title: string;
  handleClosePanel: () => void;
}) => {
  return (
    <div className={styles['panel-title']}>
      <Text size="md" fw={400}>
        {title}
      </Text>
      <IconX onClick={handleClosePanel} className={styles.closeButton} aria-label="Close" />
    </div>
  );
};

export function ActionDrawer(): JSX.Element {
  const { map } = useMapContext();
  const [panelWidth, setPanelWidth] = useState<number>(0);
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const handleZoomIn = () => {
    if (map) {
      map.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (map) {
      map.zoomOut();
    }
  };

  const handleButtonClick = (panelId: string) => {
    setPanelWidth(320);
    setActivePanel(panelId);
  };

  const handleClosePanel = () => {
    setPanelWidth(0);
    setActivePanel(null);
  };

  const actionDrawerItemsList = [
    {
      key: 'base-map-button',
      ariaLabel: 'Base Map',
      icon: <IconMap size={24} aria-label="Base Map Icon" />,
      panel: 'baseMapPanel',
      content: <></>,
      title: 'Base Map Panel Content',
    },
    {
      key: 'layers-button',
      ariaLabel: 'Layers',
      icon: <IconKeyframes size={24} aria-label="Layers Icon" />,
      panel: 'layersPanel',
      content: <LayersAndFilters />,
      title: 'Layers & Filters',
    },
    {
      key: 'info-button',
      ariaLabel: 'Info',
      icon: <IconInfoCircle size={24} aria-label="Info Icon" />,
      panel: 'infoPanel',
      content: <MapKey />,
      title: 'Map Key',
    },
    {
      key: 'drainage-network-response-button',
      ariaLabel: 'Network Simulation',
      icon: <IconRoute size={24} aria-label="Network Simulation Icon" />,
      panel: 'networkSimulation',
      content: <NetworkSimulation />,
      title: 'Network Simulation',
      marginTop: '20px',
    },
  ];

  const actionDrawerItems = actionDrawerItemsList.map((item) => (
    <div key={item.key}>
      <Tooltip text={item.ariaLabel} marginTop={item.marginTop}>
        <button
          type="button"
          className={styles[item.key]}
          onClick={() => handleButtonClick(item.panel)}
          aria-label={item.ariaLabel}
          data-active={activePanel === item.panel}
        >
          {item.icon}
        </button>
      </Tooltip>
    </div>
  ));

  const panelItems = actionDrawerItemsList.map((item) => (
    <div
      className={`${styles['side-panel']} ${activePanel === item.panel ? styles.active : ''}`}
      id={item.panel}
      key={`${item.panel}`}
    >
      <PanelTitle title={item.title} handleClosePanel={handleClosePanel} />
      <div className={styles.panelContent}>{item.content}</div>
    </div>
  ));

  return (
    <div className={styles['side-panel-container']}>
      <div className={styles['action-drawer']}>
        <div className={`${styles['side-panel-divs']} ${styles['zoom-buttons-div']}`}>
          <button
            type="button"
            className={`${styles['zoom-button']} ${styles['zoom-in']}`}
            onClick={handleZoomIn}
            aria-label="Zoom In"
          >
            <IconPlus size={32} aria-label="Zoom in icon" />
          </button>
          <button
            type="button"
            className={`${styles['zoom-button']} ${styles['zoom-out']}`}
            onClick={handleZoomOut}
            aria-label="Zoom Out"
          >
            <IconMinus size={32} aria-label="Zoom out icon" />
          </button>
        </div>
        <div className={`${styles['side-panel-divs']} ${styles['action-drawer-div']}`}>
          {actionDrawerItems}
        </div>
      </div>
      <div className={styles['transparent-panel']} />
      <div className={styles['panel-wrapper']} style={{ width: `${panelWidth}px` }}>
        <div className={styles.panelContainer}>{panelItems}</div>
      </div>
    </div>
  );
}

export default ActionDrawer;
